/**
 * Mapping validation utilities for ensuring 1:1 relationships
 */

import { ColumnMapping, ColumnMappingType } from '../components/types';

/**
 * Validate and apply mapping changes ensuring 1:1 relationships
 */
export const validateAndApplyMapping = (
  columnName: string,
  mappingType: ColumnMappingType,
  targetField: string | undefined,
  allMappings: ColumnMapping[],
  fileId: string,
  onColumnClassify: (
    columnName: string,
    type: ColumnMappingType,
    targetField?: string
  ) => void
): void => {
  // For metadata mappings, ensure 1:1 relationship
  if (mappingType === 'metadata' && targetField) {
    const existingMetadataMapping = allMappings.find(
      mapping =>
        mapping.fileId === fileId &&
        mapping.mappingType === 'metadata' &&
        mapping.metadataType?.type === targetField
    );

    // If metadata type is already mapped to a different column, remove that mapping first
    if (
      existingMetadataMapping &&
      existingMetadataMapping.columnName !== columnName
    ) {
      onColumnClassify(existingMetadataMapping.columnName, 'ignored');
    }
  }

  // For form field mappings, ensure 1:1 relationship
  if (mappingType === 'formField' && targetField) {
    const existingFormMapping = allMappings.find(
      mapping =>
        mapping.fileId === fileId &&
        mapping.mappingType === 'formField' &&
        mapping.targetField === targetField
    );

    // If field is already mapped to a different column, remove that mapping first
    if (existingFormMapping && existingFormMapping.columnName !== columnName) {
      onColumnClassify(existingFormMapping.columnName, 'ignored');
    }
  }

  // Check if the selected column is already mapped to something else
  const columnMapping = allMappings.find(
    m => m.fileId === fileId && m.columnName === columnName
  );
  if (columnMapping && columnMapping.mappingType !== 'ignored') {
    // Remove existing mapping for this column
    onColumnClassify(columnName, 'ignored');
  }

  // Apply the new mapping
  onColumnClassify(columnName, mappingType, targetField);
};

/**
 * Check if a mapping change would create conflicts
 */
export const checkMappingConflicts = (
  columnName: string,
  mappingType: ColumnMappingType,
  targetField: string | undefined,
  allMappings: ColumnMapping[],
  fileId: string
): {
  hasConflicts: boolean;
  conflicts: Array<{
    type: 'existing_metadata' | 'existing_field';
    existingColumn: string;
    target: string;
  }>;
} => {
  const conflicts: Array<{
    type: 'existing_metadata' | 'existing_field';
    existingColumn: string;
    target: string;
  }> = [];

  // Check for metadata conflicts
  if (mappingType === 'metadata' && targetField) {
    const existingMetadataMapping = allMappings.find(
      mapping =>
        mapping.fileId === fileId &&
        mapping.mappingType === 'metadata' &&
        mapping.metadataType?.type === targetField &&
        mapping.columnName !== columnName
    );

    if (existingMetadataMapping) {
      conflicts.push({
        type: 'existing_metadata',
        existingColumn: existingMetadataMapping.columnName,
        target: targetField,
      });
    }
  }

  // Check for form field conflicts
  if (mappingType === 'formField' && targetField) {
    const existingFormMapping = allMappings.find(
      mapping =>
        mapping.fileId === fileId &&
        mapping.mappingType === 'formField' &&
        mapping.targetField === targetField &&
        mapping.columnName !== columnName
    );

    if (existingFormMapping) {
      conflicts.push({
        type: 'existing_field',
        existingColumn: existingFormMapping.columnName,
        target: targetField,
      });
    }
  }

  return {
    hasConflicts: conflicts.length > 0,
    conflicts,
  };
};

/**
 * Find similar mappings across all files for bulk operations
 */
export const findSimilarMappings = (
  columnName: string,
  mappingType: ColumnMappingType,
  targetField: string | undefined,
  allMappings: ColumnMapping[]
): ColumnMapping[] => {
  if (!targetField) return [];

  return allMappings.filter(
    mapping =>
      mapping.mappingType === mappingType &&
      mapping.columnName === columnName &&
      ((mappingType === 'metadata' &&
        mapping.metadataType?.type === targetField) ||
        (mappingType === 'formField' && mapping.targetField === targetField))
  );
};
